import React, { useState, useMemo, useContext, useCallback } from 'react';
import merge from 'lodash/merge';
import {
  Button,
  Step,
  StepButton,
  Stepper,
} from '@mui/material';
import {
  and, // Used in the tester
  Categorization,
  isCategory,
  categorizationHasCategory,
  deriveLabelForUISchemaElement,
  isVisible,
  optionIs,
  RankedTester,
  rankWith,
  StatePropsOfLayout,
  uiTypeIs,
  UISchemaElement, // Import UISchemaElement for type guard
  Category, // Re-add Category as it's used in the component
  isControl,
} from '@jsonforms/core';
import {
  useJsonForms,
  TranslateProps,
  withJsonFormsLayoutProps,
  withTranslateProps,
} from '@jsonforms/react';
import {
  AjvProps,
  MaterialLayoutRenderer,
  MaterialLayoutRendererProps,
  withAjvProps,
} from '@jsonforms/material-renderers';
import { sendBroadcast } from '../utils/broadcastUtils';
import { InstanceContext } from '../utils/InstanceContext';
import { useConfirmDialog } from '../utils/ConfirmDialogContext';

export const CustomMaterialCategorizationStepperLayoutTester: RankedTester = rankWith(
  2,
  and(
    uiTypeIs('Categorization'),
    categorizationHasCategory,
    optionIs('variant', 'stepper') // This tester is correct for the stepper variant
  )
);

export interface MaterialCategorizationStepperLayoutRendererProps
  extends StatePropsOfLayout,
    AjvProps,
    TranslateProps {
  data: any;
  uischema: Categorization; // Ensure uischema is specifically Categorization for this renderer
}

export const CustomMaterialCategorizationStepperLayout = (
  props: MaterialCategorizationStepperLayoutRendererProps
) => {
  console.log('CustomMaterialCategorizationStepperLayout called')
  const {
    data,
    path,
    renderers,
    cells,
    config,
    ajv,
    t,
    uischema, // uischema is already Categorization due to interface extension
    schema,
    visible,
    readonly,
  } = props;

  const [activeCategory, setActiveCategory] = useState<number>(0);
  const instanceUUID = useContext(InstanceContext);
  const { core } = useJsonForms();
  const errors = core?.errors;
  const { confirm } = useConfirmDialog();

  const categorization = uischema as Categorization;
  const categories = useMemo(
    () =>
      (categorization.elements as UISchemaElement[]).filter( // Cast to UISchemaElement[] for filter to ensure correct type for 'element' parameter in predicate
        (element: UISchemaElement): element is Category => // Use a type guard in the filter
          isCategory(element) && isVisible(element, data, path, ajv, config)
      ), // `ajv` and `config` are from props, `data` is from props, `isVisible` is imported
    [categorization, data, path, ajv, config]
  );

  // Helper to check if specific categories have any validation errors
  const hasValidationErrors = useCallback((checkCategories: Category[]) => {
    if (!errors || errors.length === 0 || checkCategories.length === 0) return false;

    const getPaths = (elements: UISchemaElement[]): string[] => {
      let paths: string[] = [];
      elements.forEach((el: any) => {
        if (isControl(el)) {
          // Robustly convert JSON Pointer scope to AJV instance path
          // e.g. #/properties/user/properties/name -> /user/name
          const path = el.scope.replace(/^#/, '').replace(/\/properties\//g, '/');
          paths.push(path);
        }
        if (el.elements) {
          paths = paths.concat(getPaths(el.elements));
        }
      });
      return paths;
    };

    const allPaths = checkCategories.flatMap(cat => getPaths(cat.elements));
    
    return errors.some(error => {
      // 0. Root error match (e.g. data is null/uninitialized but schema expects object)
      // If there's an error at the root, it invalidates any category containing controls.
      if (error.instancePath === '' && error.keyword !== 'required' && allPaths.length > 0) {
        return true;
      }

      // 1. Direct match (the user interacted with the field and it is invalid)
      if (allPaths.includes(error.instancePath)) return true;

      // 2. Required field match (the user hasn't interacted, so AJV reports error on the parent)
      if (error.keyword === 'required' && error.params?.missingProperty) {
        const missingPath = `${error.instancePath}/${error.params.missingProperty}`;
        if (allPaths.includes(missingPath)) return true;
      }
      return false;
    });
  }, [errors]);

  const hasErrorsInCurrentCategory = useCallback(() => {
    const currentCategory = categories[activeCategory];
    return currentCategory ? hasValidationErrors([currentCategory]) : false;
  }, [activeCategory, categories, hasValidationErrors]);

  const handleStep = (step: number) => {
    const navigate = () => {
      setActiveCategory(step);
      sendBroadcast('step-navigation', { currentStep: step, instanceUUID });
    };

    // Only show confirmation if moving forward or jumping, and errors exist
    if (step > activeCategory && hasErrorsInCurrentCategory()) {
      confirm({
        message: t('stepper.incompleteFieldsMessage', "There are incomplete required fields in this section. Do you want to proceed anyway?"),
        onConfirm: navigate
      });
      return;
    }
    navigate();
  };

  const appliedUiSchemaOptions = merge({}, config, uischema.options);
  const buttonWrapperStyle = {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    width: '100%',
    padding: '16px 24px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    boxSizing: 'border-box' as const,
    zIndex: 1100,
  };
  const buttonNextStyle = {};
  const buttonStyle = {};

  const tabLabels = useMemo(() => {
    return categories.map((e: Category) => deriveLabelForUISchemaElement(e, t));
  }, [categories, t]);

  if (!visible || categories.length === 0) {
    return null;
  }

  const childProps: MaterialLayoutRendererProps = {
    elements: categories[activeCategory]?.elements || [],
    schema,
    path,
    direction: 'column',
    visible,
    renderers,
    cells,
  };

  return (
    <div style={{ paddingBottom: appliedUiSchemaOptions.showNavButtons ? '80px' : '0px' }}>
      <Stepper activeStep={activeCategory} nonLinear>
        {categories.map((_: Category, idx: number) => (
          <Step key={tabLabels[idx]}>
            <StepButton onClick={() => handleStep(idx)}>
              {tabLabels[idx]}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <div>
        <MaterialLayoutRenderer {...childProps} />
      </div>
      {appliedUiSchemaOptions.showNavButtons && (
        <div style={buttonWrapperStyle}>
          {activeCategory < categories.length - 1 ? (
            <>
              <Button
                style={buttonStyle}
                color="secondary"
                variant="contained"
                disabled={activeCategory <= 0}
                onClick={() => handleStep(activeCategory - 1)}
              >
                {t('stepper.previous', 'Previous')}
              </Button>
              <Button
                style={buttonNextStyle}
                variant="contained"
                color="primary"
                onClick={() => handleStep(activeCategory + 1)}
              >
                {t('stepper.next', 'Next')}
              </Button>
            </>
          ) : (
            <Button
              style={buttonNextStyle}
              variant="contained"
              color="primary"
              disabled={readonly}
              onClick={() => {
                const submit = () => {
                  sendBroadcast('form-submitted', { data, instanceUUID });
                  if (typeof appliedUiSchemaOptions.onSubmit === 'function') {
                    appliedUiSchemaOptions.onSubmit();
                  }
                };

                if (hasValidationErrors(categories)) {
                  confirm({
                    title: t('stepper.submissionBlockedTitle', "Submission Blocked"),
                    message: t('stepper.submissionBlockedMessage', "The form cannot be submitted due to validation errors. Please check all sections and correct them to proceed."),
                    confirmLabel: t('stepper.ok', "OK"),
                    hideCancel: true,
                  });
                  return;
                }
                submit();
              }}
            >
              {t('stepper.submit', 'Submit')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default withAjvProps(
  withTranslateProps(
    withJsonFormsLayoutProps(CustomMaterialCategorizationStepperLayout)
  )
);